/**
 * Menopause clinical core — ONE engine behind /menopause-stage,
 * /menopause-symptoms and /menopause-age.
 *
 * Why one engine: the three tools answer three different questions from the
 * SAME facts (age, cycle history, surgery, hormones). Three separate quizzes —
 * which is what every competitor ships — can and do contradict each other:
 * a symptom quiz says "you are in menopause" while a stage quiz says
 * "premenopausal", and the reader has no way to tell which one lied. Staging,
 * red flags and age assessment are therefore derived here, once.
 *
 * Sources (every threshold below is quoted, not invented):
 *  - STRAW+10: Harlow SD et al., "Executive summary of the Stages of
 *    Reproductive Aging Workshop +10", Fertil Steril 2012;97:843-51.
 *    Bleeding criteria per the ReSTAGE Collaboration.
 *  - POI: ESHRE Evidence-based guideline "Premature Ovarian Insufficiency"
 *    (2024), HCP toolkit.
 *  - MRS: Heinemann K et al., Health Qual Life Outcomes 2004;2:45 and 2:67.
 *
 * Runs in the browser (window.Meno) and under Node (module.exports) so the
 * clinical vignettes can be tested without a browser.
 */
(function (root, factory) {
    var api = factory();
    if (typeof module === 'object' && module.exports) module.exports = api;
    else root.Meno = api;
}(typeof self !== 'undefined' ? self : this, function () {
'use strict';

/* ───────────────────────── STRAW+10 staging ─────────────────────────
 * Stage ids follow the paper: -3b, -3a, -2, -1, +1a, +1b, +1c, +2.
 * `blocked` means the bleeding criteria do not apply to this woman at all —
 * STRAW+10 names these groups explicitly and refuses to stage them by
 * cycles. Every competitor quiz we checked stages them anyway.
 */

var UNSTAGEABLE = {
    /* "Women who have undergone hysterectomy or endometrial ablation cannot
       be staged by menstrual bleeding criteria" — STRAW+10. */
    hysterectomy: 'hysterectomy',
    ablation: 'ablation',
    /* "Use of hormonal therapy (including oral, injectable, or long-acting
       contraceptives) may conceal or cause amenorrhoea or irregular
       menstrual cycles" — ESHRE POI 2024. */
    hormonal: 'hormonal',
    /* "the current menstrual cycle criteria used to stage reproductive aging
       are not applicable to this population" — STRAW+10 on PCOS; the same
       passage covers hypothalamic amenorrhoea. */
    pcos: 'pcos',
    hypothalamic: 'hypothalamic',
    /* "Many medications and loss of body fat can cause amenorrhea, which can
       make the staging of the menopausal transition difficult." */
    chemo: 'chemo',
    pregnancy: 'pregnancy'
};

/**
 * Cycle-length variability per ReSTAGE, applied to real dates.
 *
 * STRAW+10 Stage -2: "increased variability in menstrual cycle length,
 * defined as a persistent difference of 7 days or more in the length of
 * consecutive cycles. Persistence is defined as recurrence within 10 cycles
 * of the first variable length cycle."
 *
 * @param {number[]} lengths consecutive cycle lengths in days, oldest first
 * @returns {{variable:boolean, firstIndex:number, recurrenceIndex:number}}
 */
function persistentVariability(lengths) {
    var out = { variable: false, firstIndex: -1, recurrenceIndex: -1 };
    if (!lengths || lengths.length < 3) return out;
    var diffs = [];
    for (var i = 1; i < lengths.length; i++) diffs.push(Math.abs(lengths[i] - lengths[i - 1]));
    for (var a = 0; a < diffs.length; a++) {
        if (diffs[a] < 7) continue;
        /* recurrence within 10 cycles of the FIRST variable-length cycle */
        for (var b = a + 1; b < diffs.length && (b - a) <= 10; b++) {
            if (diffs[b] >= 7) {
                out.variable = true; out.firstIndex = a; out.recurrenceIndex = b;
                return out;
            }
        }
    }
    return out;
}

/* A period cannot start twice on one day, cannot start tomorrow, and two
   starts less than this far apart are one period entered twice — not a cycle.
   Without this the ReSTAGE criterion happily reads a 0-day and a 5-day
   "cycle" out of a duplicated row and returns a confident stage: measured on
   a QA pass, duplicate + future dates produced "early perimenopause" from
   lengths [0, 31, 5]. Garbage in must not come out wearing a verdict. */
var MIN_PLAUSIBLE_CYCLE_DAYS = 10;

/**
 * Cycle start dates (ISO strings or Date) -> consecutive lengths in days.
 * Returns only lengths the calendar can actually produce; everything the
 * engine had to discard is reported so the page can say WHY.
 * @returns {number[]} with `.dropped` = {future, duplicate, tooShort}
 */
function cycleLengths(dates) {
    var out = [];
    out.dropped = { future: 0, duplicate: 0, tooShort: 0 };
    if (!dates || dates.length < 2) return out;

    var endOfToday = new Date();
    endOfToday.setHours(23, 59, 59, 999);

    var seen = {};
    var ds = [];
    dates.forEach(function (d) {
        var dt = (d instanceof Date) ? d : new Date(d);
        if (isNaN(dt.getTime())) return;
        if (dt > endOfToday) { out.dropped.future++; return; }
        var key = dt.toISOString().slice(0, 10);
        if (seen[key]) { out.dropped.duplicate++; return; }
        seen[key] = 1;
        ds.push(dt);
    });
    ds.sort(function (x, y) { return x - y; });

    for (var i = 1; i < ds.length; i++) {
        var len = Math.round((ds[i] - ds[i - 1]) / 86400000);
        if (len < MIN_PLAUSIBLE_CYCLE_DAYS) { out.dropped.tooShort++; continue; }
        out.push(len);
    }
    return out;
}

/**
 * @param {object} p profile
 *   age                    number
 *   surgery                'none'|'hysterectomy'|'ablation'|'bso'|'unilateral'
 *   hormones               'none'|'contraception'|'iud_hormonal'|'mht'
 *   conditions             array of 'pcos'|'hypothalamic'|'chemo'|'pregnancy'
 *   monthsSinceLastPeriod  number
 *   cycleDates             ISO date strings of period starts (precise lane)
 *   cyclePattern           'regular'|'shorter'|'variable'|'skipped' (recall lane)
 */
function stage(p) {
    p = p || {};
    var res = {
        stage: null, phase: null, blocked: null, basis: null,
        criterion: null, canGetPregnant: null, notes: []
    };

    /* 1. Surgical menopause: bilateral oophorectomy is a diagnosis on its own.
       "Bilateral oophorectomy = diagnosis of POI, no further testing needed"
       (ESHRE 2024) when it happened before 40. */
    if (p.surgery === 'bso') {
        res.stage = 'surgical'; res.phase = 'postmenopause';
        res.basis = 'surgery'; res.criterion = 'bso';
        res.canGetPregnant = false;
        return res;
    }
    if (p.surgery === 'hysterectomy' || p.surgery === 'ablation') {
        res.blocked = (p.surgery === 'hysterectomy') ? UNSTAGEABLE.hysterectomy : UNSTAGEABLE.ablation;
        res.basis = 'blocked';
        return res;
    }

    var conds = p.conditions || [];
    for (var i = 0; i < conds.length; i++) {
        if (UNSTAGEABLE[conds[i]]) { res.blocked = UNSTAGEABLE[conds[i]]; res.basis = 'blocked'; return res; }
    }
    if (p.hormones && p.hormones !== 'none') {
        res.blocked = UNSTAGEABLE.hormonal; res.basis = 'blocked';
        return res;
    }

    var m = (typeof p.monthsSinceLastPeriod === 'number') ? p.monthsSinceLastPeriod : null;

    /* 2. Postmenopause: the FMP is identified retrospectively after 12 months
       of amenorrhoea. Substages are counted from the FMP ITSELF, which is `m`
       months ago — not from the point at which the 12-month rule confirmed it.
       STRAW+10: "Stage +1a marks the end of the 12-month period of amenorrhea
       required to define that the FMP has occurred. It corresponds to the end
       of 'perimenopause' ... which begins at Stage -2 and ends 12 months after
       the FMP." So +1a IS that first year, +1b the second (the two together
       "estimated to last, on average, 2 years"), +1c a further 3-6 years, and
       early postmenopause totals ~5-8 years; we take 8 as the +1c/+2 boundary.
       Counting from the confirmation point instead put +1b and +1c a whole
       year late — the first version of this file did exactly that. */
    if (m !== null && m >= 12) {
        var yearsSinceFmp = m / 12;
        res.phase = 'postmenopause';
        res.basis = 'cycles';
        res.criterion = 'amenorrhea_12m';
        res.canGetPregnant = false;
        if (yearsSinceFmp < 1) res.stage = '+1a';
        else if (yearsSinceFmp < 2) res.stage = '+1b';
        else if (yearsSinceFmp < 8) res.stage = '+1c';
        else res.stage = '+2';
        res.yearsSinceFmp = yearsSinceFmp;
        return res;
    }

    /* 3. Late menopausal transition: "marked by the occurrence of amenorrhea
       of 60 days or longer". */
    var skipped = (m !== null && m >= 2) || p.cyclePattern === 'skipped';
    if (skipped) {
        res.stage = '-1'; res.phase = 'perimenopause';
        res.basis = (m !== null) ? 'cycles' : 'recall';
        res.criterion = 'amenorrhea_60d';
        res.canGetPregnant = true;
        return res;
    }

    /* 4. Early menopausal transition: persistent >=7 day difference between
       consecutive cycles. Prefer real dates over recall when we have them. */
    var lens = cycleLengths(p.cycleDates);
    if (lens.length >= 3) {
        var v = persistentVariability(lens);
        if (v.variable) {
            res.stage = '-2'; res.phase = 'perimenopause';
            res.basis = 'dates'; res.criterion = 'variability_7d';
            res.canGetPregnant = true;
            res.evidence = { lengths: lens, firstIndex: v.firstIndex, recurrenceIndex: v.recurrenceIndex };
            return res;
        }
        /* Regular by the STRAW criterion. -3a is "subtle changes ... specifically
           shorter cycles"; the paper leaves -3b/-3a cycle criteria unquantified
           (its own research priority #2), so we only separate them when the
           woman reports her cycles got shorter. */
        res.stage = (p.cyclePattern === 'shorter') ? '-3a' : '-3b';
        res.phase = 'reproductive';
        res.basis = 'dates'; res.criterion = 'regular';
        res.canGetPregnant = true;
        res.evidence = { lengths: lens };
        return res;
    }

    if (p.cyclePattern === 'variable') {
        res.stage = '-2'; res.phase = 'perimenopause';
        res.basis = 'recall'; res.criterion = 'variability_7d';
        res.canGetPregnant = true;
        return res;
    }
    if (p.cyclePattern === 'shorter') {
        res.stage = '-3a'; res.phase = 'reproductive';
        res.basis = 'recall'; res.criterion = 'shorter';
        res.canGetPregnant = true;
        return res;
    }
    if (p.cyclePattern === 'regular') {
        res.stage = '-3b'; res.phase = 'reproductive';
        res.basis = 'recall'; res.criterion = 'regular';
        res.canGetPregnant = true;
        return res;
    }
    res.basis = 'insufficient';
    return res;
}

/* ───────────────────────── MRS ─────────────────────────
 * 11 items, 0-4 each, total 0-44. Subscales per the MRS manual:
 * somatic 1,2,3,11 (0-16); psychological 4,5,6,7 (0-16);
 * urogenital 8,9,10 (0-12).
 */
var MRS_SOMATIC = [0, 1, 2, 10];      /* zero-based item indices */
var MRS_PSYCHO = [3, 4, 5, 6];
var MRS_UROGEN = [7, 8, 9];

/* Heinemann 2004 (HQLO 2:67) severity bands. The total-score upper boundary
   is published as 9-15/16+ in that validation paper, while the MRS manual's
   table is quoted elsewhere as 9-16/17+. We use the validation paper because
   it is the source we could read in full, and we always show the score itself
   so a woman sitting on the boundary is never given a band without its
   number. */
var MRS_BANDS = {
    total:      [[0, 4], [5, 8], [9, 15], [16, Infinity]],
    somatic:    [[0, 2], [3, 4], [5, 7], [8, Infinity]],
    psycho:     [[0, 1], [2, 3], [4, 6], [7, Infinity]],
    urogenital: [[0, 0], [1, 1], [2, 3], [4, Infinity]]
};
var BAND_NAMES = ['none', 'mild', 'moderate', 'severe'];

function band(kind, value) {
    var b = MRS_BANDS[kind];
    for (var i = 0; i < b.length; i++) if (value >= b[i][0] && value <= b[i][1]) return BAND_NAMES[i];
    return BAND_NAMES[0];
}

function sumItems(answers, idx) {
    var s = 0;
    for (var i = 0; i < idx.length; i++) s += (answers[idx[i]] || 0);
    return s;
}

/** @param {number[]} answers 11 values 0..4 */
function mrs(answers) {
    answers = answers || [];
    var somatic = sumItems(answers, MRS_SOMATIC);
    var psycho = sumItems(answers, MRS_PSYCHO);
    var urogenital = sumItems(answers, MRS_UROGEN);
    var total = somatic + psycho + urogenital;
    var frac = [
        { k: 'somatic', v: somatic / 16 },
        { k: 'psycho', v: psycho / 16 },
        { k: 'urogenital', v: urogenital / 12 }
    ].sort(function (a, b2) { return b2.v - a.v; });
    return {
        total: total, somatic: somatic, psycho: psycho, urogenital: urogenital,
        max: { total: 44, somatic: 16, psycho: 16, urogenital: 12 },
        band: {
            total: band('total', total), somatic: band('somatic', somatic),
            psycho: band('psycho', psycho), urogenital: band('urogenital', urogenital)
        },
        /* Which domain is worst RELATIVE to its own ceiling — the number a
           woman and her doctor actually act on. Raw subscale totals cannot be
           compared across domains because the ceilings differ (16/16/12). */
        dominant: frac[0].v === 0 ? null : frac[0].k
    };
}

/* MRS treatment-response threshold: the validation study defines success as
   "5 and more points reduction after therapy compared with baseline". */
var MRS_RESPONSE_DROP = 5;

function mrsChange(baselineTotal, currentTotal) {
    var d = baselineTotal - currentTotal;
    return { delta: d, improved: d >= MRS_RESPONSE_DROP, worsened: d <= -MRS_RESPONSE_DROP };
}

/* ───────────────────────── Age / POI ─────────────────────────
 * ESHRE 2024 prevalences: POI 3.7%, early menopause 12.2%,
 * usual age of menopause 45-55.
 */
var AGE_BANDS = { poi: 40, early: 45, usualLow: 45, usualHigh: 55 };
var PREVALENCE = { poi: 3.7, early: 12.2 };

/**
 * Classify an age at (or approaching) menopause and decide whether the POI
 * pathway applies. Deliberately NOT a predictor: individual age at menopause
 * cannot be predicted, and pretending otherwise is the single most common
 * defect in this segment.
 */
/* The age field is where a typo becomes a diagnosis. HTML min/max validate
   nothing on their own: a QA pass typed 999 and got a confident "late
   postmenopause". Outside this range the engine refuses rather than guesses. */
var AGE_MIN = 18, AGE_MAX = 99;

function ageIsUsable(age) {
    return typeof age === 'number' && isFinite(age) && age >= AGE_MIN && age <= AGE_MAX;
}

/* ── FSH: reading the result, not just asking for it ─────────────────────
 * ESHRE POI guideline 2024, diagnostic criteria (all three required, age < 40):
 *   - oligo/amenorrhoea for at least 4 months;
 *   - FSH above 25 IU/L;
 *   - measured on TWO occasions at least 4 weeks apart.
 * NICE NG23: FSH must NOT be used to diagnose menopause over 45 with typical
 * symptoms — an honest "this test will not tell you anything" is worth more to
 * her than a number.
 * Exogenous hormones suppress FSH, so on the pill or MHT the value cannot be
 * read at all; that is a refusal, not a low result.
 */
var FSH_POI_THRESHOLD = 25;     // IU/L (ESHRE 2024)
var FSH_REPEAT_WEEKS = 4;       // "at least four weeks apart"
var FSH_USELESS_FROM_AGE = 45;  // NICE NG23
var FSH_MIN_PLAUSIBLE = 0.1, FSH_MAX_PLAUSIBLE = 300;

function fshValueIsUsable(v) {
    return typeof v === 'number' && isFinite(v) && v >= FSH_MIN_PLAUSIBLE && v <= FSH_MAX_PLAUSIBLE;
}

function _fshDay(d) {
    if (!d) return null;
    var dt = (d instanceof Date) ? d : new Date(d);
    return isNaN(dt.getTime()) ? null : Math.floor(dt.getTime() / 86400000);
}

/**
 * Read one or two FSH results against the published criterion.
 *
 * @param {object} p  { age, hormones, monthsCycleDisturbed | monthsSinceLastPeriod,
 *                      fsh: [{ value:Number, date:'YYYY-MM-DD' }, ...] }
 * @returns {object}  { verdict, high, usable, needsSecondAfter, monthsShort, threshold }
 *   verdict is one of:
 *     'no_values'          — nothing entered
 *     'bad_values'         — entered, but outside anything a lab reports
 *     'not_interpretable'  — on hormones: FSH is suppressed, the number says nothing
 *     'not_needed'         — 45+: the guideline says do not use this test here
 *     'below_threshold'    — the high-FSH part of the criterion is not met
 *     'one_high'           — one value above threshold; the second draw has a date
 *     'too_close'          — two high values, but less than four weeks apart
 *     'months_short'       — two high values four weeks apart, but the cycle part
 *                            (four months) is not met yet
 *     'criterion_met'      — every published part is met; the DIAGNOSIS is still a
 *                            doctor's, and the copy must say so
 */
function fshAssessment(p) {
    p = p || {};
    var out = {
        verdict: 'no_values', usable: 0, high: 0,
        needsSecondAfter: null, monthsShort: null, threshold: FSH_POI_THRESHOLD
    };

    var raw = p.fsh || [];
    var vals = [];
    var entered = 0;
    for (var i = 0; i < raw.length; i++) {
        var r = raw[i] || {};
        if (r.value === null || r.value === undefined || r.value === '') continue;
        entered++;
        if (!fshValueIsUsable(r.value)) continue;
        vals.push({ value: r.value, day: _fshDay(r.date) });
    }
    if (!vals.length) {
        out.verdict = entered ? 'bad_values' : 'no_values';
        return out;
    }
    out.usable = vals.length;
    out.high = vals.filter(function (v) { return v.value > FSH_POI_THRESHOLD; }).length;

    /* Hormones first: a suppressed value is not a low value, and treating it as
       one would tell her the criterion is not met when in truth it cannot be
       read. */
    if (p.hormones && p.hormones !== 'none') { out.verdict = 'not_interpretable'; return out; }

    if (ageIsUsable(p.age) && p.age >= FSH_USELESS_FROM_AGE) { out.verdict = 'not_needed'; return out; }

    if (!out.high) { out.verdict = 'below_threshold'; return out; }

    var highs = vals.filter(function (v) { return v.value > FSH_POI_THRESHOLD; })
                    .filter(function (v) { return v.day !== null; })
                    .sort(function (a, b) { return a.day - b.day; });

    if (out.high < 2) {
        /* One high value: the criterion asks for a second one, and it has an
           EARLIEST date. Naming that date is the whole point — "repeat it later"
           is what she has already been told. */
        if (highs.length) {
            out.needsSecondAfter = new Date((highs[0].day + FSH_REPEAT_WEEKS * 7) * 86400000)
                .toISOString().slice(0, 10);
        }
        out.verdict = 'one_high';
        return out;
    }

    var gap = highs.length >= 2 ? (highs[highs.length - 1].day - highs[0].day) : null;
    if (gap === null) { out.verdict = 'one_high'; return out; }
    if (gap < FSH_REPEAT_WEEKS * 7) {
        out.needsSecondAfter = new Date((highs[0].day + FSH_REPEAT_WEEKS * 7) * 86400000)
            .toISOString().slice(0, 10);
        out.verdict = 'too_close';
        return out;
    }

    var months = p.monthsCycleDisturbed;
    if (typeof months !== 'number') months = p.monthsSinceLastPeriod;
    if (typeof months === 'number' && months < 4) {
        out.monthsShort = Math.max(0, Math.round((4 - months) * 10) / 10);
        out.verdict = 'months_short';
        return out;
    }

    out.verdict = 'criterion_met';
    return out;
}

function ageAssessment(p) {
    p = p || {};
    var age = p.age;
    var out = { category: null, flags: [], needsFshTesting: false, prevalence: PREVALENCE };
    if (!ageIsUsable(age)) { out.category = age === undefined || age === null ? null : 'age_out_of_range'; return out; }

    var st = p.stageResult || stage(p);
    var reachedMenopause = st.phase === 'postmenopause';
    var monthsDisturbed = p.monthsCycleDisturbed;
    if (typeof monthsDisturbed !== 'number' && typeof p.monthsSinceLastPeriod === 'number') {
        monthsDisturbed = p.monthsSinceLastPeriod;
    }

    /* POI: "FSH > 25IU/L + oligo/amenorrhoea for at least 4 months = DIAGNOSIS",
       under age 40. Symptoms of estrogen deficiency "may or MAY NOT be present"
       — which is exactly why a symptom quiz cannot find POI. */
    if (age < AGE_BANDS.poi) {
        if (p.surgery === 'bso') {
            out.category = 'poi_surgical';
            out.flags.push('poi_confirmed_by_surgery');
            return out;
        }
        if (typeof monthsDisturbed === 'number' && monthsDisturbed >= 4) {
            out.category = 'poi_suspected';
            out.needsFshTesting = true;
            out.flags.push('poi_suspected');
            if (p.hormones && p.hormones !== 'none') out.flags.push('hormones_mask_fsh');
            return out;
        }
        out.category = 'under40_no_criteria';
        return out;
    }

    if (age < AGE_BANDS.early && reachedMenopause) {
        out.category = 'early_menopause';
        out.flags.push('early_menopause');
        return out;
    }
    if (reachedMenopause) {
        out.category = (age > AGE_BANDS.usualHigh) ? 'late_menopause' : 'usual_menopause';
        return out;
    }

    /* "Not yet" is a CLAIM, and it is only available when the cycle could be
       read at all. On hormones, after hysterectomy or ablation, with PCOS or
       after chemotherapy the bleeding pattern says nothing about the ovaries —
       so answering "menopause has not arrived" there would be inventing
       reassurance. Measured on a live vignette: 43 years old, no periods for
       14 months, on hormonal contraception was told exactly that. */
    if (st.blocked) {
        out.category = 'undetermined';
        out.blocked = st.blocked;
        return out;
    }

    out.category = 'not_yet';
    return out;
}

/* ───────────────────────── Red flags ─────────────────────────
 * Deliberately short. Each one is a situation where the correct action
 * differs from "wait and see".
 */
function redFlags(p) {
    p = p || {};
    var st = p.stageResult || stage(p);
    var flags = [];

    /* Any bleeding after 12 months of amenorrhoea needs evaluation. */
    if (st.phase === 'postmenopause' && p.bleedingSinceMenopause) flags.push('postmenopausal_bleeding');

    /* Perimenopause is still fertile: STRAW+10 places the end of
       "perimenopause" 12 months AFTER the FMP. */
    if (st.canGetPregnant && st.phase === 'perimenopause'
        && p.wantsPregnancy === false && (!p.hormones || p.hormones === 'none')
        && !p.contraception) flags.push('contraception_still_needed');

    var merged = {};
    for (var k in p) if (Object.prototype.hasOwnProperty.call(p, k)) merged[k] = p[k];
    merged.stageResult = st;
    var ageA = ageAssessment(merged);
    if (ageA.category === 'poi_suspected') flags.push('poi_suspected');
    if (ageA.category === 'poi_surgical') flags.push('poi_surgical');
    if (ageA.category === 'early_menopause') flags.push('early_menopause');

    /* POI: "HRT does not prevent natural conception" and natural conception
       occurs in 5-15%, so contraception must still be offered. Not applicable
       after bilateral oophorectomy. */
    if (ageA.category === 'poi_suspected' && p.wantsPregnancy === false
        && !p.contraception) flags.push('poi_contraception');

    if (p.heavyBleeding) flags.push('heavy_bleeding');

    return flags;
}

return {
    stage: stage,
    mrs: mrs,
    mrsChange: mrsChange,
    ageAssessment: ageAssessment,
    redFlags: redFlags,
    cycleLengths: cycleLengths,
    persistentVariability: persistentVariability,
    ageIsUsable: ageIsUsable,
    fshAssessment: fshAssessment,
    fshValueIsUsable: fshValueIsUsable,
    constants: {
        MRS_BANDS: MRS_BANDS, MRS_SOMATIC: MRS_SOMATIC, MRS_PSYCHO: MRS_PSYCHO,
        MRS_UROGEN: MRS_UROGEN, MRS_RESPONSE_DROP: MRS_RESPONSE_DROP,
        AGE_BANDS: AGE_BANDS, PREVALENCE: PREVALENCE, UNSTAGEABLE: UNSTAGEABLE,
        AGE_MIN: AGE_MIN, AGE_MAX: AGE_MAX, MIN_PLAUSIBLE_CYCLE_DAYS: MIN_PLAUSIBLE_CYCLE_DAYS,
        FSH_POI_THRESHOLD: FSH_POI_THRESHOLD, FSH_REPEAT_WEEKS: FSH_REPEAT_WEEKS,
        FSH_USELESS_FROM_AGE: FSH_USELESS_FROM_AGE
    }
};
}));
